import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useTenantCreationRequest } from '@/hooks/use-tenant-creation-request';
import {
  mergePreviewState,
  OnboardingPreviewState,
} from '@/pages/onboarding/merge-preview-state';

type SetOnboardingPreview = (patch: Partial<OnboardingPreviewState>) => void;

const OnboardingPreviewStateContext =
  createContext<OnboardingPreviewState | null>(null);
const SetOnboardingPreviewContext = createContext<SetOnboardingPreview | null>(
  null
);

export function useOnboardingPreview(): OnboardingPreviewState {
  const context = useContext(OnboardingPreviewStateContext);

  if (context === null) {
    throw new Error(
      'useOnboardingPreview must be used within an OnboardingPreviewProvider'
    );
  }

  return context;
}

export function useSetOnboardingPreview(): SetOnboardingPreview {
  const context = useContext(SetOnboardingPreviewContext);

  if (context === null) {
    throw new Error(
      'useSetOnboardingPreview must be used within an OnboardingPreviewProvider'
    );
  }

  return context;
}

export function OnboardingPreviewProvider({ children }: PropsWithChildren) {
  const tenantCreationRequest = useTenantCreationRequest();
  const [overrides, setOverrides] = useState<Partial<OnboardingPreviewState>>(
    {}
  );

  // Stable across renders — consumers (e.g. NotificationPreviewPage's
  // debounced preview-sync effect) put this in a `useEffect` dependency
  // array, and a fresh function identity on every render would retrigger
  // that effect on every render it causes, which calls this and causes
  // another render: an infinite loop.
  const setPreview: SetOnboardingPreview = useCallback((patch) => {
    setOverrides((current) => ({ ...current, ...patch }));
  }, []);

  const state = mergePreviewState(tenantCreationRequest, overrides);

  return (
    <OnboardingPreviewStateContext.Provider value={state}>
      <SetOnboardingPreviewContext.Provider value={setPreview}>
        {children}
      </SetOnboardingPreviewContext.Provider>
    </OnboardingPreviewStateContext.Provider>
  );
}
